import { useState, useEffect, useRef } from "react";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import VenueWrapper from "./components/VenueWrapper";
import VenueDocInterface from "./components/VenueDocInterface";
import AuthSection from "./components/AuthSection";
import CONFIG from "./config";
import "./i18n";
import { useTranslation } from "react-i18next";
import { isTokenExpired, getTimeUntilExpiration } from "./utils/tokenUtils";
import { refreshTokens } from "./utils/tokenRefresh";

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const logoutRef = useRef(null);
  const [idToken, setIdToken] = useState(null);
  const [currentView, setCurrentView] = useState("dashboard");
  const [currentInterface] = useState("documents"); // Default to documents interface

  // Apply default theme
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    applyMode(savedTheme === "dark" ? Mode.Dark : Mode.Light);
  }, []);

  // Check for existing session and validate token
  useEffect(() => {
    const checkSession = async () => {
      // First, check for OAuth callback (authorization code in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code && CONFIG.midwayEnabled) {
        console.log("OAuth callback detected, exchanging code for tokens...");
        try {
          // Exchange authorization code for tokens
          const tokenEndpoint = `https://${CONFIG.cognitoDomain}/oauth2/token`;
          const redirectUri = window.location.origin + CONFIG.callbackPath;

          const response = await fetch(tokenEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              grant_type: "authorization_code",
              client_id: CONFIG.userPoolClientId,
              code: code,
              redirect_uri: redirectUri,
            }),
          });

          if (response.ok) {
            const tokens = await response.json();

            // Store tokens
            localStorage.setItem("idToken", tokens.id_token);
            localStorage.setItem("accessToken", tokens.access_token);
            localStorage.setItem("refreshToken", tokens.refresh_token);
            localStorage.setItem("loginMechanism", "midway");

            // Parse user info from ID token
            const payload = JSON.parse(atob(tokens.id_token.split(".")[1]));
            const userEmail = payload.email || payload["cognito:username"];

            localStorage.setItem("currentUser", userEmail);
            setIdToken(tokens.id_token);
            setCurrentUser(userEmail);
            setIsAuthenticated(true);

            // Initialize usage tracker for Midway login
            if (window.UsageTrackerAuto) {
              try {
                // eslint-disable-next-line no-unused-vars
                const tracker = new window.UsageTrackerAuto();
                console.log("Usage tracker initialized for Midway login");
              } catch (error) {
                console.error("Failed to initialize usage tracker:", error);
              }
            }

            // Clean up URL (remove code parameter)
            window.history.replaceState({}, document.title, window.location.pathname);
            setIsLoading(false);
            return;
          } else {
            console.error("Token exchange failed:", response.status);
            const errorData = await response.json().catch(() => ({}));
            console.error("Error details:", errorData);
          }
        } catch (error) {
          console.error("OAuth callback error:", error);
        }
      }

      // Check for existing session
      const storedToken = localStorage.getItem("idToken");
      const storedUser = localStorage.getItem("currentUser");

      if (storedToken && storedUser) {
        // Check if token is expired — try to refresh before giving up
        if (isTokenExpired(storedToken)) {
          console.log("Stored token is expired, attempting refresh...");
          const refreshed = await refreshTokens();
          if (refreshed) {
            setIdToken(refreshed.idToken);
            setCurrentUser(storedUser);
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
          }
          console.log("Token refresh failed, clearing session");
          localStorage.removeItem("idToken");
          localStorage.removeItem("currentUser");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("loginMechanism");
          setIsLoading(false);
          return;
        }

        setIdToken(storedToken);
        setCurrentUser(storedUser);
        setIsAuthenticated(true);

        // Initialize usage tracker for existing session
        if (window.UsageTrackerAuto) {
          try {
            // eslint-disable-next-line no-unused-vars
            const tracker = new window.UsageTrackerAuto();
            console.log("Usage tracker initialized for existing session");
          } catch (error) {
            console.error("Failed to initialize usage tracker:", error);
          }
        }
      }
      setIsLoading(false);
    };

    checkSession();
  }, []);

  const handleLoginSuccess = (token, user) => {
    // Validate token before storing
    if (isTokenExpired(token)) {
      console.error("Received expired token, rejecting login");
      return;
    }

    localStorage.setItem("idToken", token);
    localStorage.setItem("currentUser", user);
    localStorage.setItem("loginMechanism", "cognito");
    setIdToken(token);
    setCurrentUser(user);
    setIsAuthenticated(true);

    // Initialize usage tracker after successful login
    if (window.UsageTrackerAuto) {
      try {
        // eslint-disable-next-line no-unused-vars
        const tracker = new window.UsageTrackerAuto();
        console.log("Usage tracker initialized successfully");
      } catch (error) {
        console.error("Failed to initialize usage tracker:", error);
      }
    }
  };

  const handleLogout = () => {
    console.log("handleLogout called - clearing session");
    localStorage.removeItem("idToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("loginMechanism");
    setIdToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // Keep logout ref updated
  logoutRef.current = handleLogout;

  // Global fetch interceptor to catch 401/403 responses and attempt token refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        // If we get a 401 or 403, try refreshing the token first
        if (response.status === 401 || response.status === 403) {
          console.log("Global fetch interceptor: Unauthorized response, attempting token refresh...");
          const refreshed = await refreshTokens();
          if (refreshed) {
            // Retry the original request with the new token
            const [url, options = {}] = args;
            const retryOptions = {
              ...options,
              headers: {
                ...options.headers,
                Authorization: refreshed.idToken,
              },
            };
            setIdToken(refreshed.idToken);
            return originalFetch(url, retryOptions);
          }
          // Refresh failed — log out
          setTimeout(() => {
            if (logoutRef.current) {
              logoutRef.current();
            }
          }, 0);
        }

        return response;
      } catch (error) {
        // Network errors (like CORS-blocked 401s) throw exceptions
        const url = args[0];
        if (url && typeof url === "string" && url.includes(CONFIG.apiUrl)) {
          console.log("Global fetch interceptor: Network error on API call, attempting token refresh...");
          const refreshed = await refreshTokens();
          if (refreshed) {
            const [reqUrl, options = {}] = args;
            const retryOptions = {
              ...options,
              headers: {
                ...options.headers,
                Authorization: refreshed.idToken,
              },
            };
            setIdToken(refreshed.idToken);
            return originalFetch(reqUrl, retryOptions);
          }
          setTimeout(() => {
            if (logoutRef.current) {
              logoutRef.current();
            }
          }, 0);
        }
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated]);

  // Proactive token refresh — refresh before expiry, logout only if refresh fails
  useEffect(() => {
    if (!isAuthenticated || !idToken) return;

    const attemptRefresh = async () => {
      const refreshed = await refreshTokens();
      if (refreshed) {
        setIdToken(refreshed.idToken);
        localStorage.setItem("idToken", refreshed.idToken);
      } else {
        console.log("Token refresh failed, logging out");
        handleLogout();
      }
    };

    // If already expired, try refresh immediately
    if (isTokenExpired(idToken)) {
      attemptRefresh();
      return;
    }

    // Schedule refresh 2 minutes before expiry
    const timeUntilExpiration = getTimeUntilExpiration(idToken);
    const refreshIn = Math.max(0, (timeUntilExpiration - 120) * 1000);
    console.log(`Token expires in ${timeUntilExpiration}s, scheduling refresh in ${Math.round(refreshIn / 1000)}s`);

    const refreshTimeout = setTimeout(attemptRefresh, refreshIn);

    // Also check periodically in case the tab was suspended
    const checkInterval = setInterval(() => {
      if (isTokenExpired(idToken, 120)) {
        attemptRefresh();
      }
    }, 30000);

    return () => {
      clearTimeout(refreshTimeout);
      clearInterval(checkInterval);
    };
  }, [isAuthenticated, idToken]);

  // Check token validity when tab becomes visible or localStorage changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenValidity = async () => {
      const storedToken = localStorage.getItem("idToken");

      // If token was removed from localStorage, logout
      if (!storedToken) {
        console.log("Token removed from localStorage, logging out");
        handleLogout();
        return;
      }

      // If token is expired, try refresh
      if (isTokenExpired(storedToken)) {
        console.log("Token expired on visibility change, attempting refresh...");
        const refreshed = await refreshTokens();
        if (refreshed) {
          setIdToken(refreshed.idToken);
        } else {
          handleLogout();
        }
        return;
      }

      // If stored token differs from current token, update state
      if (storedToken !== idToken) {
        console.log("Token changed in localStorage, updating state");
        setIdToken(storedToken);
      }
    };

    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("Tab became visible, checking token validity");
        checkTokenValidity();
      }
    };

    // Check when localStorage changes (e.g., from another tab or devtools)
    const handleStorageChange = (e) => {
      if (e.key === "idToken" || e.key === "currentUser") {
        console.log("localStorage changed, checking token validity");
        checkTokenValidity();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated, idToken]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>{t("app.common.loading")}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthSection onLoginSuccess={handleLoginSuccess} config={CONFIG} />;
  }

  const isAdmin = currentUser && currentUser.endsWith("@amazon.com");

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  console.log("App.js render - currentInterface:", currentInterface, "currentView:", currentView, "isAdmin:", isAdmin);

  return (
    <VenueWrapper onLogout={handleLogout} currentView={currentView} onNavigate={handleNavigate} isAdmin={isAdmin} currentInterface={currentInterface}>
      {(settingsSection) => {
        return (
          <VenueDocInterface
            additionalNavItems={settingsSection}
            onLogout={handleLogout}
            currentView={currentView}
            onNavigate={handleNavigate}
            isAdmin={isAdmin}
            currentInterface={currentInterface}
            idToken={idToken}
            currentUser={currentUser}
          />
        );
      }}
    </VenueWrapper>
  );
}

export default App;
